import { createContext, useContext, useEffect, useState } from "react";
import { useSettings } from "./SettingsProvider";
import { useUser } from "./UserProvider";
import { getRange } from "../utils";

const UserDataContext = createContext({
  sets: {},
  collection: [],
  collectionRange: { start: 0, end: 50 },
  activeSet: "",
  fetchLoadSets: async () => {
    throw new Error("Not authenticated");
  },
  setFunctions: {
    selectSet() {},
    changeSetName() {},
    removeSet() {},
    removeAll() {},
    addSetToSet() {},
    addToSet() {},
    removeFromSet() {},
    loadCollectionRange() {},
  },
});

export const UserDataProvider = ({ children }) => {
  const { settings } = useSettings();
  const { auth, fetchAuth, logout } = useUser();

  // Data
  const [sets, setSets] = useState({});
  const [collection, setCollection] = useState([]);
  const [collectionRange, setCollectionRange] = useState({ start: 0, end: 50 });
  const [collectionCount, setCollectionCount] = useState(0);
  const [activeSet, setActiveSet] = useState("");

  const fetchUserData = async (url, options) => {
    try {
      const res = await fetchAuth(url, {
        body: { lang: settings.lang, ...options.body },
      })

      if (!res.ok) {
        return
      }

      const body = await res.json();

      setSets(body.overview);
      setCollectionRange(body.range);
      setCollection(body.collection);
      setCollectionCount(body.count);
      setActiveSet(body.active);

    } catch (error) {
      console.log("Request failed", error);
    }
  };

  const defaultRange = { start: 0, end: 50 };
  const fetchLoadSets = (data = {}) =>
    fetchUserData(`${process.env.REACT_APP_API_URL}/sets/load`, {
      body: { active: activeSet, range: defaultRange, ...data },
    });

  const fetchStoreSet = (data) =>
    fetchUserData(`${process.env.REACT_APP_API_URL}/sets/store`, {
      body: data,
    });

  const fetchStoreSelectSet = (data) =>
    fetchUserData(`${process.env.REACT_APP_API_URL}/sets/storeSelect`, {
      body: data,
    });

  const setFunctions = {
    selectSet(collection) {
      fetchLoadSets({ active: collection });
    },

    changeSetName(from, to) {
      fetchStoreSet({
        type: "rename",
        collection: from,
        new_name: to,
        active: to,
        range: defaultRange,
      });
    },

    removeSet(collection) {
      fetchStoreSet({
        type: "delete",
        collection: collection,
        active: activeSet,
        range: defaultRange,
      });
    },

    removeAll(collection) {
      fetchStoreSet({
        type: "empty",
        collection: collection,
        active: activeSet,
        range: defaultRange,
      });
    },

    addSetToSet(target, source) {
      fetchStoreSet({
        type: "add",
        collection: target,
        source_collection: source,
        active: activeSet,
        range: defaultRange,
      });
    },

    addToSet(collection, keys, filterIds = undefined, filterTerms = undefined) {
      if (filterIds) {
        fetchStoreSelectSet({
          type: collection in sets ? "add" : "create",
          filterIds: filterIds,
          filterTerms: filterTerms,
          active: activeSet,
          collection: collection,
          range: defaultRange,
        });
      } else {
        fetchStoreSet({
          type: collection in sets ? "add" : "create",
          collection: collection,
          keys: keys,
          active: activeSet,
          range: defaultRange,
        });
      }
    },

    removeFromSet(collection, keys) {
      fetchStoreSet({
        type: "remove",
        collection: collection,
        keys: keys,
        active: activeSet,
        range: defaultRange,
      });
    },

    loadCollectionRange(type) {
      const range = getRange(type, collectionRange, 50, collectionCount);
      fetchLoadSets({ range: range });
    },
  };

  useEffect(() => {
    fetchLoadSets({
      range: {
        start: collectionRange.start,
        end: collectionRange.end > 0 ? collectionRange.end : 50,
      },
    });
  }, [
    auth.username,
    settings.lang,
    collectionRange.start,
    collectionRange.end,
  ]);

  return (
    <UserDataContext.Provider
      value={{
        sets,
        collection,
        collectionRange,
        collectionCount,
        activeSet,
        fetchLoadSets,
        setFunctions,
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
};

export const useUserData = () => useContext(UserDataContext);
